// Canvas spell check handler - enhances canvas instances without modifying prototypes

/**
 * Enhance a canvas instance with spell check handling capabilities
 * This avoids modifying the fabric.Canvas prototype
 * @param {fabric.Canvas} canvas - The canvas instance to enhance
 */
export function enhanceCanvasWithSpellCheck(canvas) {
  if (!canvas || canvas._spellCheckEnhanced) {
    return;
  }

  // Mark as enhanced to avoid double enhancement
  canvas._spellCheckEnhanced = true;

  // Store original methods
  const original_checkTarget = canvas._checkTarget;
  const original_onMouseDown = canvas._onMouseDown;

  // Override _checkTarget to allow control targeting during editing
  canvas._checkTarget = function (pointer, obj, globalPointer) {
    // Special handling for textbox in editing mode
    if (
      obj &&
      obj.type === 'textbox' &&
      obj.isEditing &&
      obj.hasSpellCheckErrors &&
      obj.hasSpellCheckErrors()
    ) {
      // Check if we're over the spell check control
      const corner = obj._findTargetCorner(pointer);
      if (corner === 'fixSpelling') {
        // Allow the control to be targeted
        return true;
      }
    }

    // Use default behavior for everything else
    return original_checkTarget.call(this, pointer, obj, globalPointer);
  };

  // Override _onMouseDown to handle spell check control clicks during editing
  canvas._onMouseDown = function (e) {
    // Check if we're clicking on a textbox's spell check control while it's editing
    const activeObject = this.getActiveObject();
    if (
      activeObject &&
      activeObject.type === 'textbox' &&
      activeObject.isEditing &&
      activeObject.hasSpellCheckErrors &&
      activeObject.hasSpellCheckErrors()
    ) {
      const pointer = this.getPointer(e);
      const corner = activeObject._findTargetCorner(pointer);

      if (corner === 'fixSpelling' && activeObject.controls.fixSpelling) {
        // Handle the spell check control click
        const control = activeObject.controls.fixSpelling;
        if (control.mouseUpHandler) {
          control.mouseUpHandler(e, { target: activeObject });
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    // Call original method for normal behavior
    original_onMouseDown.call(this, e);
  };
}

/**
 * Remove spell check enhancements from a canvas
 * @param {fabric.Canvas} canvas - The canvas instance to clean up
 */
export function removeCanvasSpellCheckEnhancement(canvas) {
  if (!canvas || !canvas._spellCheckEnhanced) {
    return;
  }

  // Restore original methods if stored
  if (canvas._original_checkTarget) {
    canvas._checkTarget = canvas._original_checkTarget;
  }
  if (canvas._original_onMouseDown) {
    canvas._onMouseDown = canvas._original_onMouseDown;
  }

  delete canvas._spellCheckEnhanced;
}
